<script setup lang="ts">
import { fileIcon, formatFileSize, formatUploadDate } from '@/components/documents/fileMeta'
import FinestButton from '@/components/FinestButton.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import { useDocumentUpload } from '@/composables/useDocumentUpload'
import { missingWhen } from '@/utils/field-state'
import AttachmentController from '@controllers/AttachmentController'
import { useConfirm } from 'primevue/useconfirm'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AttachmentData = App.Data.AttachmentData
import DocumentSectionData = App.Data.Documents.DocumentSectionData
import DocumentTypeOptionData = App.Data.Documents.DocumentTypeOptionData

const props = defineProps<{
  section: DocumentSectionData
  typeOptions: DocumentTypeOptionData[]
  contextLabel?: string
}>()

const { t, locale } = useI18n()
const confirm = useConfirm()
const { uploading, upload, classify, remove } = useDocumentUpload()

const fileInput = ref<HTMLInputElement | null>(null)
const dropTargetActive = ref(false)

function onSelect(event: Event): void {
  const input = event.target as HTMLInputElement

  handOver([...(input.files ?? [])])
  input.value = ''
}

function onDrop(event: DragEvent): void {
  dropTargetActive.value = false

  handOver([...(event.dataTransfer?.files ?? [])])
}

function handOver(files: File[]): void {
  if (!files.length) {
    return
  }

  void upload({ attachableType: props.section.attachableType, attachableId: props.section.attachableId }, files)
}

function downloadUrl(document: AttachmentData): string | undefined {
  return document.id ? AttachmentController.download(document.id).url : undefined
}

function fileMeta(document: AttachmentData): string {
  return [formatFileSize(document.size, locale.value), formatUploadDate(document.updated_at, locale.value)]
    .filter(Boolean)
    .join(' · ')
}

function chooseType(document: AttachmentData, documentType: string | null): void {
  if (documentType && document.id) {
    classify(document.id, documentType)
  }
}

function confirmRemove(document: AttachmentData): void {
  if (!document.id) {
    return
  }

  confirm.require({
    header: t('delete.header'),
    message: t('delete.message', { filename: document.filename }),
    rejectProps: { label: t('delete.reject'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('delete.accept'), severity: 'danger' },
    accept: () => remove(document.id!),
  })
}
</script>

<template>
  <section class="fh-card p-6">
    <header class="mb-4 flex flex-wrap items-center gap-3">
      <i class="pi pi-folder-open text-ink-3" aria-hidden="true" />
      <h2 class="text-base font-bold">
        {{ contextLabel ? `${t('heading')} · ${contextLabel}` : t('heading') }}
      </h2>
      <span class="bg-surface-3 text-ink-2 ml-auto rounded-full px-2.5 py-1 text-xs font-medium">
        {{ t('count', section.documents.length) }}
      </span>
    </header>

    <p class="text-ink-3 mb-4 text-sm">{{ t('hint') }}</p>

    <ul v-if="section.documents.length" class="divide-line-2 mb-4 divide-y">
      <li v-for="document in section.documents" :key="document.id" class="flex flex-wrap items-center gap-3 py-3">
        <i :class="fileIcon(document)" class="text-ink-3" aria-hidden="true" />

        <div class="min-w-0 flex-1">
          <a :href="downloadUrl(document)" class="text-action block truncate font-medium hover:underline">
            {{ document.filename }}
          </a>
          <p class="text-ink-3 text-sm">{{ fileMeta(document) }}</p>
        </div>

        <FinestSelect
          :model-value="document.description ?? null"
          :options="typeOptions"
          :placeholder="t('chooseType')"
          :state="missingWhen(!document.description)"
          :aria-label="t('chooseType')"
          class="w-64"
          @change="(value) => chooseType(document, value)" />

        <FinestButton
          variant="ghost"
          size="sm"
          icon="pi pi-trash"
          :label="t('actions.remove')"
          @click="confirmRemove(document)" />
      </li>
    </ul>

    <div
      class="flex flex-wrap items-center justify-center gap-3 rounded-xl border border-dashed p-5 text-center transition-colors"
      :class="dropTargetActive ? 'border-action bg-info-subtle' : 'border-line'"
      @dragover.prevent="dropTargetActive = !uploading"
      @dragleave.prevent="dropTargetActive = false"
      @drop.prevent="onDrop">
      <p class="text-ink-2 text-sm">{{ t('drop') }}</p>
      <FinestButton
        variant="secondary"
        size="sm"
        icon="pi pi-plus"
        :label="t('actions.choose')"
        :disabled="uploading"
        @click="fileInput?.click()" />
    </div>

    <input ref="fileInput" type="file" multiple class="hidden" @change="onSelect" />
  </section>
</template>

<i18n lang="json">
{
  "de": {
    "heading": "Dokumente",
    "hint": "Alle Uploads sind freiwillig. Nach dem Hochladen wählst du die Art des Dokuments.",
    "count": "Keine Dateien | {count} Datei | {count} Dateien",
    "chooseType": "Art wählen",
    "drop": "Dateien hierher ziehen oder",
    "actions": {
      "choose": "Dateien auswählen",
      "remove": "Entfernen"
    },
    "delete": {
      "header": "Dokument entfernen",
      "message": "„{filename}“ wird entfernt.",
      "reject": "Abbrechen",
      "accept": "Entfernen"
    }
  },
  "en": {
    "heading": "Documents",
    "hint": "All uploads are voluntary. After uploading you pick the type of the document.",
    "count": "No files | {count} file | {count} files",
    "chooseType": "Choose type",
    "drop": "Drag files here or",
    "actions": {
      "choose": "Choose files",
      "remove": "Remove"
    },
    "delete": {
      "header": "Remove document",
      "message": "“{filename}” will be removed.",
      "reject": "Cancel",
      "accept": "Remove"
    }
  }
}
</i18n>
